import { create } from 'zustand';
import { clientProductControllerApi } from '@api/api-config.ts';
import { ClientProductDetailResponse, PriceVariantDTO, SupplierPreference } from '@api/generated/search-service';
import { findCheapestPriceVariant } from '@utils/priceVariants.ts';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';
import { ProductDetailTab } from '@features/product-detail/types.ts';
import { notifyProductTileUpdate } from '@features/product-detail/productTileUpdates.ts';

type ProductDetailState = {
  productDetail: ClientProductDetailResponse | null;
  selectedVariant: PriceVariantDTO | null;
  isExpiredPrice: boolean;

  isLoading: boolean;
  error: string | null;
  productId: number | null;
  loadProductDetail: (productId: number, selectedTab?: ProductDetailTab) => Promise<void>;
  setSelectedVariant: (variant: PriceVariantDTO) => void;
  setSupplierPreference: (productId: number, supplierId: number, preference: SupplierPreference | null) => Promise<void>;
  clearProductDetail: () => void;
};

export const useProductDetail = create<ProductDetailState>(set => ({
  productDetail: null,
  isLoading: false,
  error: null,
  productId: null,
  selectedVariant: null,
  isExpiredPrice: false,
  loadProductDetail: async (productId: number) => {
    set({ isLoading: true, error: null, productId, productDetail: null });

    try {
      // Použití getState() místo hooků - hooky nelze volat uvnitř zustand store funkcí
      const { portalId, orgId: organizationId } = useOrgPortal.getState();

      // Čtení supplierIds přímo z URL místo hooku
      const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const selectedSupplierIds = searchParams?.getAll('supplierIds') || [];

      const response = await clientProductControllerApi.getProductDetail({
        productId,
        portalId,
        organizationId,
      });

      // Označení dodavatelů (srdíčko / nechtěný) rozhoduje o předvybrané variantě před cenou.
      const preferenceBySupplierId: Record<number, SupplierPreference | undefined> = {};
      // Den závozu dodavatele - proti němu se posuzuje platnost ceny, ne proti dnešku.
      const deliveryDateBySupplierId: Record<number, string | undefined> = {};
      response.data.productSuppliers?.forEach(t => {
        preferenceBySupplierId[t.supplierId] = t.supplierPreference;
        deliveryDateBySupplierId[t.supplierId] = t.deliveryDate;
      });

      const { variant, isExpired } = findCheapestPriceVariant(
        response.data.productSuppliers?.map(t => t.priceVariants).flat(),
        selectedSupplierIds?.map(t => parseInt(t)),
        preferenceBySupplierId,
        deliveryDateBySupplierId,
      );

      set({
        productDetail: response.data,
        isLoading: false,
        error: null,
        selectedVariant: variant,
        isExpiredPrice: isExpired,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při načítání produktu',
        isLoading: false,
        productDetail: null,
      });
    }
  },

  setSelectedVariant: (variant: PriceVariantDTO) => {
    set({ selectedVariant: variant });
  },

  /**
   * preference = null označení zruší. Po uložení se detail načte znovu, ať sedí pořadí i výběr,
   * a čerstvý stav se pošle dlaždicím v seznamu pod modalem.
   */
  setSupplierPreference: async (productId: number, supplierId: number, preference: SupplierPreference | null) => {
    const { portalId, orgId: organizationId } = useOrgPortal.getState();

    await clientProductControllerApi.setProductSupplierPreference({
      productId,
      supplierId,
      portalId,
      organizationId,
      setSupplierPreferenceRequest: { preference: preference ?? undefined },
    });

    await useProductDetail.getState().loadProductDetail(productId);

    const detail = useProductDetail.getState().productDetail;
    if (detail?.productId === productId) {
      notifyProductTileUpdate({
        kind: 'preference',
        productId,
        supplierPreferences: Object.fromEntries(detail.productSuppliers.map(s => [s.supplierId, s.supplierPreference ?? null])),
        hasSupplierPreference: detail.hasSupplierPreference,
      });
    }
  },

  clearProductDetail: () => {
    set({
      productDetail: null,
      error: null,
      isLoading: false,
      productId: null,
    });
  },
}));
